import { FormKitNode } from "@formkit/core";
import { globalAxios, QFood } from "@q-food/q-food";
import { property } from "lodash";
import { renderStringTemplate } from "../renderStringTemplate";
import { useUserStore } from "~stores/User.ts";

export function replaceVariables(uri: string, nodeProps: FormKitNode["props"], form?: FormKitNode) {
	const { attrs } = nodeProps;

	const replacer = (it: string, variable?: string) => {
		if (variable?.startsWith("$values")) {
			const key = variable.replace("$values.", "");
			if (form?.find(key, "name")?.value) {
				return String(form?.find(key, "name")?.value);
			}

			return "";
		}
		if (variable?.startsWith("$node")) {
			return String(property(variable.slice(1))(nodeProps));
		}
		if (variable?.startsWith("$") && attrs.dependencies) {
			const attrKey = Object.keys(attrs.dependencies).find(
				(key) => variable.toLowerCase().replace(/[$.]/g, "") === key.toLowerCase(),
			);
			if (attrKey) {
				return attrs.dependencies[attrKey];
			}
		}

		return it;
	};
	return uri
		.split("+")
		.map((it) => it.trim())
		.join("")
		.replaceAll(/'/g, "")
		.replaceAll(/(\$.+?)(?=[^\w\d\[\]\.\&\?])/g, replacer)
		.replace(/(\$.+?)$/g, replacer);
}

// Factory function to create a loader to hydrate the autocomplete
export function createAutocompleteOptionLoader<T = unknown>(
	resourceURI: string,
	options: {
		optionLabelTemplate: string;
	} = {
		optionLabelTemplate: "{{ name }}",
	},
) {
	const { optionLabelTemplate } = options;
	return async (id: string, cachedOption: T) => {
		if (cachedOption) return cachedOption;

		try {
			const url = new URL(resourceURI, QFood.BASE_URL);
			url.searchParams.delete("criteria");

			const { data, status } = await globalAxios.get(`${url.toString()}/${id}`);
			if (data && status >= 200 && status < 300) {
				if (!("id" in data)) {
					throw new Error("The response does not have an id property");
				}

				return {
					value: data.id,
					label: renderStringTemplate(optionLabelTemplate, data),
				};
			}
		} catch (err) {
			console.error(err);
			return { label: "Error loading" };
		}
	};
}

// Factory function to create a loader for the autocomplete's options
export function createAutocompleteOptionsLoader(
	resourceURI: string,
	rootForm?: FormKitNode<any>,
	options: {
		searchProps: string[];
		optionsLabelTemplate: string;
	} = {
		searchProps: ["name"],
		optionsLabelTemplate: "{{ name }}",
	},
) {
	const { optionsLabelTemplate, searchProps } = options;
	return async (nodeProps: FormKitNode["props"]) => {
		// If not search, load page one unless load more is clicked
		const { search, page, hasNextPage, attrs } = nodeProps;

		const resource = replaceVariables(resourceURI, nodeProps, rootForm);

		const { activeCompany, smartTabs } = useUserStore();
		const url = new URL(resource, QFood.BASE_URL);
		const criteria = url.searchParams.get("criteria") ?? "";
		const includeNumberSearch =
			resourceURI.includes("/relations") || resourceURI.includes("/products") || resourceURI.includes("/packagings");
		const searchValue = search?.replace(/\\/g, "\\\\")?.replace(/'/, "\\'");
		if (activeCompany.name.includes("Frozen") && resourceURI.includes("/products") && search) {
			const query = `name=simplesearch='${searchValue}' or number=='${searchValue}*'`;
			url.searchParams.set("criteria", `${criteria ? criteria + ";" : ""}` + `(${query})`);
		} else if (resourceURI.includes("/products") && search) {
			const query = `name=simplesearch='${searchValue}' or number=='${searchValue}'`;
			url.searchParams.set("criteria", `${criteria ? criteria + ";" : ""}` + `(${query})`);
		} else if (resourceURI.includes("/countries") && search) {
			const query = `name=='*${searchValue}*' or shortCode=='*${searchValue}*'`;
			url.searchParams.set("criteria", `${criteria ? criteria + ";" : ""}` + `(${query})`);
		} else if (resourceURI.includes("/currencies") && search) {
			const query = `name=='*${searchValue}*' or id=='*${searchValue}*'`;
			url.searchParams.set("criteria", `${criteria ? criteria + ";" : ""}` + `(${query})`);
		} else if (search && searchProps.length >= 1) {
			const query = searchProps
				.map((prop) => {
					if (prop.startsWith("*") && prop.endsWith("*")) {
						return `${prop.replace(/\*+/g, "")}=='*${searchValue}*'`;
					}
					if (prop.startsWith("*")) {
						return `${prop.replace(/\*+/g, "")}=='*${searchValue}'`;
					}
					if (prop.endsWith("*")) {
						return `${prop.replace(/\*+/g, "")}=='${searchValue}*'`;
					}

					return `${prop}=='${searchValue}'`;
				})
				.concat(includeNumberSearch ? [`number=='${searchValue}'`] : [])
				.join(" or ");

			url.searchParams.set("criteria", `${criteria ? criteria + ";" : ""}` + `(${query})`);
		}

		if (page) {
			url.searchParams.set("page", (page - 1).toString());
		}

		const { data } = await globalAxios.get(url.toString());

		if (!data.last) hasNextPage();

		if (Array.isArray(data.content)) {
			return data.content.map((it: any) => {
				return {
					value: it.id,
					label: renderStringTemplate(optionsLabelTemplate, it),
				};
			});
		}

		return [];
	};
}
